import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

/**
 * Admin feature-flags tab hooks (DUE-180).
 *
 * Schemas mirror the live FastAPI shapes in `src/api/schemas/admin_flags.py`:
 *   - `FlagDTO`         — GET/POST `/api/admin/flags`, PATCH `/api/admin/flags/{key}`
 *   - `FlagOverrideRow` — GET/PUT `/api/admin/flags/{key}/overrides`
 *
 * `rules` is `list[dict[str, Any]]` on the backend (kept loose there so a
 * malformed/future rule shape doesn't 500 the evaluator — see
 * `FeatureFlagService.rule_matches`). This admin editor only ever writes the
 * one shape it renders (`{attribute, values, enabled}`), so the schema here
 * is the strict `RuleSchema` rather than `z.record(z.unknown())` — that way
 * a shape desync fails the query instead of silently rendering garbage.
 */

export const RULE_ATTRIBUTES = ["platform", "subscription_tier", "locale"] as const;
export type RuleAttribute = (typeof RULE_ATTRIBUTES)[number];

export const RuleSchema = z.object({
  attribute: z.enum(RULE_ATTRIBUTES),
  values: z.array(z.string()),
  enabled: z.boolean(),
});
export type Rule = z.infer<typeof RuleSchema>;

export const FlagSchema = z.object({
  key: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  rules: z.array(RuleSchema),
  overrides_count: z.number().int().nonnegative(),
});
export type Flag = z.infer<typeof FlagSchema>;
const FlagListSchema = z.array(FlagSchema);

export const FlagOverrideRowSchema = z.object({
  user_id: z.number().int(),
  tg_id: z.number().int().nullable(),
  username: z.string().nullable(),
  enabled: z.boolean(),
});
export type FlagOverrideRow = z.infer<typeof FlagOverrideRowSchema>;
const FlagOverrideListSchema = z.array(FlagOverrideRowSchema);

const KEY = ["admin", "flags"];

function overridesKey(key: string): unknown[] {
  return [...KEY, key, "overrides"];
}

export function useFlags() {
  return useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) =>
      request({ path: "/api/admin/flags", schema: FlagListSchema, signal }),
  });
}

export function useCreateFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { key: string; description?: string | null }) =>
      request({
        method: "POST",
        path: "/api/admin/flags",
        body,
        schema: FlagSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePatchFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      key: string;
      enabled?: boolean;
      description?: string | null;
      rules?: Rule[];
    }) => {
      const { key, ...patch } = args;
      return request({
        method: "PATCH",
        path: `/api/admin/flags/${encodeURIComponent(key)}`,
        body: patch,
        schema: FlagSchema,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useFlagOverrides(key: string | null) {
  return useQuery({
    queryKey: overridesKey(key ?? ""),
    queryFn: ({ signal }) =>
      request({
        path: `/api/admin/flags/${encodeURIComponent(key ?? "")}/overrides`,
        schema: FlagOverrideListSchema,
        signal,
      }),
    enabled: key !== null,
  });
}

export function usePutOverride(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id: number; enabled: boolean }) =>
      request({
        method: "PUT",
        path: `/api/admin/flags/${encodeURIComponent(key)}/overrides`,
        body,
        schema: FlagOverrideRowSchema,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: overridesKey(key) });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteOverride(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) =>
      request({
        method: "DELETE",
        path: `/api/admin/flags/${encodeURIComponent(key)}/overrides/${userId}`,
        schema: z.null(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: overridesKey(key) });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
